/**
 * 양식 레지스트리 · 기관 연결 패널 (환경설정 / 사업계획 양식관리 공용)
 * SSOT: D:\295\erp\CM\design\05_양식레지스트리_매칭.md
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { useAuth } from 'contexts/AuthContext';
import { Card, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, ActionButtonGroup } from 'components/forms';
import BizFormHwpPreviewDialog from 'components/budget/BizFormHwpPreviewDialog';
import { showAlert } from 'utils/toastBridge';
import { DOC_TYPE, DOC_TYPE_LABEL, DOC_TYPE_OPTIONS } from 'system/formDocTypes';
import {
  hydrateFormRegistry,
  ensureFormRegistry,
  listForms,
  getTenantFormBindings,
  bindFormToTenant,
  setTenantActiveForm,
  resolveForm,
  listMatchRules,
  dbLinkedFields,
  formationLinkedFields,
  registerFormUpload,
  replaceFormTemplate,
} from 'system/formRegistryStore';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  padding: 10px 12px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th,
  td {
    border: 1px solid var(--erp-border);
    padding: 4px 8px;
    text-align: left;
  }
  th {
    background: var(--erp-surface-muted);
    font-weight: 600;
  }
  tr.active td {
    background: var(--erp-color-bg-selected, var(--erp-surface-muted));
  }
`;

const Meta = styled.p`
  margin: 0;
  font-size: 11px;
  color: var(--erp-text-secondary);
`;

const UploadRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 6px;
`;

const FileInput = styled.input`
  font-size: 12px;
  max-width: 220px;
`;

const TextInput = styled.input`
  min-height: 26px;
  padding: 3px 8px;
  font-size: 12px;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
  width: 200px;
  box-sizing: border-box;
`;

const SelectMini = styled.select`
  min-height: 26px;
  padding: 3px 8px;
  font-size: 12px;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
`;

/**
 * @param {{ fixedDocType?: string, hideDocTypeFilter?: boolean, embedded?: boolean }} props
 */
export default function FormRegistryPanel({
  fixedDocType = '',
  hideDocTypeFilter = false,
  embedded = false,
}) {
  const tenantCode = useTenantCode();
  const { user } = useAuth();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const [docType, setDocType] = useState(fixedDocType || DOC_TYPE.BIZ_PLAN);
  const [forms, setForms] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [rules, setRules] = useState([]);
  const [activeFormId, setActiveFormId] = useState('');
  const [selectedFormId, setSelectedFormId] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [copyFromId, setCopyFromId] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (fixedDocType) setDocType(fixedDocType);
  }, [fixedDocType]);

  const methods = useForm({
    defaultValues: { tenantCode, docType: fixedDocType || DOC_TYPE.BIZ_PLAN, formId: '' },
  });
  const { setValue, getValues } = methods;

  const refresh = useCallback(async () => {
    await hydrateFormRegistry();
    const dt = fixedDocType || getValues('docType') || DOC_TYPE.BIZ_PLAN;
    setDocType(dt);
    const list = listForms({ docType: dt });
    setForms(list);
    const slot = getTenantFormBindings(tenantCode, dt);
    setBindings(slot?.bindings || []);
    const active = resolveForm(dt, { tenantCode });
    setActiveFormId(active?.formId || '');
    setSelectedFormId((prev) => {
      if (prev && list.some((f) => f.formId === prev)) return prev;
      return active?.formId || list[0]?.formId || '';
    });
    setRules(listMatchRules({ docType: dt, tenantCode }));
    setValue('tenantCode', tenantCode);
    setValue('docType', dt);
    setValue('formId', active?.formId || list[0]?.formId || '');
  }, [tenantCode, fixedDocType, getValues, setValue]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selected = forms.find((f) => f.formId === selectedFormId) || null;

  const handleBindActive = useCallback(() => {
    const dt = fixedDocType || getValues('docType') || docType;
    const formId = getValues('formId') || selectedFormId;
    if (!formId) {
      showAlert('양식을 선택하세요.', 'warning');
      return;
    }
    bindFormToTenant(tenantCode, dt, formId, { makeActive: true });
    setTenantActiveForm(tenantCode, dt, formId);
    refresh();
    showAlert(
      `${DOC_TYPE_LABEL[dt] || dt} · 기관 ${tenantCode} ← ${formId} 활성 연결`,
      'success',
    );
  }, [fixedDocType, getValues, selectedFormId, tenantCode, docType, refresh]);

  const handlePreview = useCallback(() => {
    const form = forms.find((f) => f.formId === (getValues('formId') || selectedFormId));
    if (!form) {
      showAlert('미리볼 양식을 선택하세요.', 'warning');
      return;
    }
    if (!form.publicUrl && !form.hasUploadedTemplate) {
      showAlert('미리볼 파일(URL 또는 업로드분)이 없습니다.', 'warning');
      return;
    }
    setSelectedFormId(form.formId);
    setPreviewOpen(true);
  }, [forms, getValues, selectedFormId]);

  const allFormsForCopy = useMemo(() => listForms({ docType }), [docType, forms]);

  const handleUpload = useCallback(async () => {
    if (!uploadFile) {
      showAlert('HWP 파일을 선택하세요.', 'warning');
      return;
    }
    setUploadBusy(true);
    try {
      const meta = await registerFormUpload({
        docType,
        formName: uploadName,
        file: uploadFile,
        copyFieldsFromFormId: copyFromId || undefined,
        tenantCode,
        makeActive: true,
      });
      if (!(meta.fields || []).length) {
        showAlert(
          `등록했습니다 (${meta.formId}). 필드맵이 비어 있습니다. 복사 원본을 지정해 다시 올리거나 P3에서 편집하세요.`,
          'warning',
        );
      } else {
        showAlert(`양식 등록·활성 연결: ${meta.formId}`, 'success');
      }
      setUploadFile(null);
      setUploadName('');
      if (fileRef.current) fileRef.current.value = '';
      setSelectedFormId(meta.formId);
      refresh();
    } catch (e) {
      showAlert(e?.message || '업로드 실패', 'error');
    } finally {
      setUploadBusy(false);
    }
  }, [uploadFile, uploadName, copyFromId, docType, tenantCode, refresh]);

  const handleReplaceTemplate = useCallback(async () => {
    const formId = getValues('formId') || selectedFormId;
    if (!formId) {
      showAlert('교체할 양식을 선택하세요.', 'warning');
      return;
    }
    if (!uploadFile) {
      showAlert('HWP 파일을 선택하세요.', 'warning');
      return;
    }
    setUploadBusy(true);
    try {
      await replaceFormTemplate(formId, uploadFile);
      showAlert(`템플릿을 교체했습니다: ${formId}`, 'success');
      setUploadFile(null);
      if (fileRef.current) fileRef.current.value = '';
      refresh();
    } catch (e) {
      showAlert(e?.message || '교체 실패', 'error');
    } finally {
      setUploadBusy(false);
    }
  }, [getValues, selectedFormId, uploadFile, refresh]);

  const bindFields = useMemo(() => {
    const row = [
      {
        name: 'tenantCode',
        label: '기관',
        type: 'text',
        width: 120,
        labelWidth: SEARCH_LABEL_W,
        readonly: true,
      },
    ];
    if (!hideDocTypeFilter && !fixedDocType) {
      row.push({
        name: 'docType',
        label: '업무',
        type: 'select',
        width: 160,
        labelWidth: SEARCH_LABEL_W,
        options: DOC_TYPE_OPTIONS,
      });
    }
    row.push({
      name: 'formId',
      label: '양식',
      type: 'select',
      width: 300,
      labelWidth: SEARCH_LABEL_W,
      options: [
        { value: '', label: '선택' },
        ...forms.map((f) => ({ value: f.formId, label: `${f.formName} (${f.formId})` })),
      ],
    });
    return [row];
  }, [forms, hideDocTypeFilter, fixedDocType]);

  const actionButtons = [
    {
      id: 'refresh',
      label: '새로고침',
      onClick: () => {
        const dt = getValues('docType') || docType;
        setDocType(dt);
        refresh();
      },
    },
    {
      id: 'preview',
      label: '양식 미리보기',
      onClick: handlePreview,
      disabled: !selected,
    },
    {
      id: 'bind',
      label: '기관에 연결(활성)',
      onClick: handleBindActive,
      variant: 'success',
    },
    {
      id: 'upload',
      label: uploadBusy ? '업로드 중…' : 'HWP 등록',
      onClick: handleUpload,
      disabled: uploadBusy || !uploadFile,
      title: '새 formId로 등록하고 이 기관 활성 양식으로 연결',
    },
    {
      id: 'replace',
      label: '선택 양식 파일교체',
      onClick: handleReplaceTemplate,
      disabled: uploadBusy || !uploadFile || !selectedFormId,
      title: '선택한 formId의 템플릿 HWP만 교체',
    },
  ];

  return (
    <Stack>
      {!embedded ? null : (
        <Meta>
          공통 양식 레지스트리. HWP 업로드·기관 활성 연결. 매칭·ACL은 환경설정 › 매칭 규칙·사용 권한. prefs=
          {prefsUserId}
        </Meta>
      )}
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <FormArea
          dense
          fields={bindFields}
          onFormChange={(formData, _prev, changed) => {
            if (changed === 'docType' && formData?.docType) {
              setDocType(formData.docType);
              setSelectedFormId('');
              setTimeout(refresh, 0);
            }
          }}
        />
      </FormProvider>
      <Meta>
        업무={DOC_TYPE_LABEL[docType] || docType} · 활성={activeFormId || '—'} · 매칭규칙{' '}
        {rules.length}건 · HWP 등록 후 기관 활성 연결 · 목록 더블클릭으로 미리보기
      </Meta>

      <Panel>
        <strong style={{ fontSize: 13 }}>HWP 업로드 (P1)</strong>
        <Meta>
          새 양식 등록 시 필드맵은 아래 「복사 원본」에서 가져옵니다. 원본 없으면 맵이 비고 작성
          팝업이 동작하지 않을 수 있습니다.
        </Meta>
        <UploadRow>
          <label>
            파일{' '}
            <FileInput
              ref={fileRef}
              type="file"
              accept=".hwp,.hwpx,application/octet-stream"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setUploadFile(f);
                if (f && !uploadName) setUploadName(f.name.replace(/\.hwpx?$/i, ''));
              }}
            />
          </label>
          <label>
            양식명{' '}
            <TextInput
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder="표시 이름"
            />
          </label>
          <label>
            필드맵 복사{' '}
            <SelectMini
              value={copyFromId}
              onChange={(e) => setCopyFromId(e.target.value)}
              style={{ minWidth: CommonCss.w120 }}
            >
              <option value="">없음</option>
              {allFormsForCopy.map((f) => (
                <option key={f.formId} value={f.formId}>
                  {f.formName} ({(f.fields || []).length})
                </option>
              ))}
            </SelectMini>
          </label>
        </UploadRow>
      </Panel>

      <Panel>
        <strong style={{ fontSize: 13 }}>양식 카탈로그</strong>
        <Table>
          <thead>
            <tr>
              <th>docType</th>
              <th>formId</th>
              <th>양식명</th>
              <th>파일</th>
              <th>필드</th>
              <th>연결</th>
              <th>활성</th>
              <th>원본</th>
            </tr>
          </thead>
          <tbody>
            {forms.map((f) => {
              const bound = bindings.some((b) => b.formId === f.formId);
              const isActive = activeFormId === f.formId;
              return (
                <tr
                  key={f.formId}
                  className={selectedFormId === f.formId ? 'active' : ''}
                  onClick={() => {
                    setSelectedFormId(f.formId);
                    setValue('formId', f.formId);
                  }}
                    onDoubleClick={() => {
                      setSelectedFormId(f.formId);
                      setValue('formId', f.formId);
                      if (f.publicUrl || f.hasUploadedTemplate) setPreviewOpen(true);
                      else showAlert('이 양식에 파일이 없습니다.', 'warning');
                    }}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{DOC_TYPE_LABEL[f.docType] || f.docType}</td>
                  <td>{f.formId}</td>
                  <td>{f.formName}</td>
                  <td>{f.templateFile || f.publicUrl}</td>
                  <td>{(f.fields || []).length}</td>
                  <td>{bound ? 'Y' : 'N'}</td>
                  <td>{isActive ? 'Y' : ''}</td>
                  <td>{f.hasUploadedTemplate ? '업로드' : f.publicUrl ? 'URL' : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Panel>

      <Panel>
        <strong style={{ fontSize: 13 }}>
          필드맵 {selected ? `· ${selected.formName}` : ''}
        </strong>
        {!selected ? (
          <Meta>목록에서 양식을 선택하세요.</Meta>
        ) : (
          <>
            <Meta>
              DB연동 {dbLinkedFields(selected).length}
              {selected.docType === DOC_TYPE.BIZ_PLAN
                ? ` · 편성연동 ${formationLinkedFields(selected).length}`
                : ''}{' '}
              · map v{selected.mapVersion || 1} ·{' '}
              {selected.hasUploadedTemplate ? 'IndexedDB 업로드' : selected.publicUrl || '—'}
            </Meta>
            <Table>
              <thead>
                <tr>
                  <th>fieldId</th>
                  <th>라벨</th>
                  <th>입력</th>
                  <th>DB</th>
                  <th>안내</th>
                </tr>
              </thead>
              <tbody>
                {(selected.fields || []).map((fld) => (
                  <tr key={fld.fieldId}>
                    <td>
                      <code>{fld.fieldId}</code>
                    </td>
                    <td>{fld.label}</td>
                    <td>{fld.input}</td>
                    <td>{fld.syncToDb ? 'Y' : ''}</td>
                    <td>{fld.guide}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Panel>

      {rules.length > 0 ? (
        <Panel>
          <strong style={{ fontSize: 13 }}>매칭 규칙 (요약)</strong>
          <Meta>편집은 환경설정 › 양식 › 매칭 규칙</Meta>
          <Table>
            <thead>
              <tr>
                <th>ruleId</th>
                <th>formId</th>
                <th>부서</th>
                <th>사업</th>
                <th>재원</th>
                <th>priority</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.ruleId}>
                  <td>{r.ruleId}</td>
                  <td>{r.formId}</td>
                  <td>{r.deptCode || '*'}</td>
                  <td>{r.projectCode || '*'}</td>
                  <td>{r.fundCode || '*'}</td>
                  <td>{r.priority || 0}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      ) : null}

      <BizFormHwpPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        form={selected}
        prefsUserId={prefsUserId}
      />
    </Stack>
  );
}
